import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box, Paper, Button, Dialog, DialogContent, DialogTitle, IconButton, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from '@contexts/SnackbarContext';
import API from '@utils/axios';
import Layout from '@components/Layout';
import MailingListForm from '@components/mailinglist/MailingListForm';
import ContactTable from '@components/contact/ContactTable';
import ContactCard from '@components/contact/ContactCard';
import ContactForm from '@components/contact/ContactForm';
import ContactCreationForm from '@components/contact/ContactCreationForm';
import FormSkeleton from '@components/FormSkeleton';
import { formError } from '@utils/helpers';

function MailingListEditPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { showMessage } = useSnackbar();
    const [initialData, setInitialData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [openedContact, setOpenedContact] = useState(null);
    const [isEditingContact, setIsEditingContact] = useState(false);
    const [tableKey, setTableKey] = useState(0);
    const formRef = useRef();

    const fetchData = useCallback(async () => {
        try {
            const response = await API.get(`/mailing-lists/${id}`);
            setInitialData(response.data);
        } catch (error) {
            showMessage(t('Failed to load data'), 'error');
        } finally {
            setLoaded(true);
        }
    }, [id, t, showMessage]);

    const onSubmit = async (data) => {
        try {
            await API.put(`/mailing-lists/${id}`, data);
            showMessage(t('Mailing list updated successfully'), 'success');
        } catch (error) {
            showMessage(t('Failed to update mailing list'), 'error');
        }
    }

    const handleContactSubmit = async (data) => {
        try {
            const response = await API.put(`/mailing-lists/${id}/contacts/${openedContact.id}`, data);
            showMessage(t('Contact updated successfully'), 'success');
            setIsEditingContact(false);
            // Mettre à jour le contact avec les données du formulaire
            setOpenedContact(prev => ({
                ...prev,
                ...response.data
            }));
            // Forcer le rafraîchissement du tableau
            setTableKey(prev => prev + 1);
        } catch (error) {
            formError(error, t, formRef.current.setError, showMessage);
        }
    }

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const openContactDialog = (params) => {
        setOpenedContact(params.row);
        setIsEditingContact(false);
    }

    const handleClose = () => {
        setOpenedContact(null);
        setIsEditingContact(false);
    }

    return (
        <Layout withFloatingButton={true}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/mailinglists" underline="hover">{t('Mailing list management')}</Link>
                    <Typography>{t('View mailing list')}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to="/mailinglists" underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            {loaded ?
                <Box sx={{ mb: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <MailingListForm formData={initialData} onSubmit={onSubmit}
                            buttons={
                                <Box display="flex" justifyContent="space-between">
                                    <Button type="submit" variant="contained" color="primary">
                                        {t('Save')}
                                    </Button>
                                </Box>
                            } />
                    </Paper>
                    <Typography variant="h3" component="h3" sx={{ mt: 4, mb: 1 }}>{t('Contacts')}</Typography>
                    <ContactTable 
                        key={tableKey}
                        url={`/mailing-lists/${id}/contacts`} 
                        onRowClick={openContactDialog} 
                        withDeleteButton={true} 
                        onDeleted={fetchData} 
                    />
                    {initialData.count.contacts_deleted > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {initialData.count.contacts_deleted > 1
                                ? t('{{count}} contacts of this mailing list have been deleted since its creation', { count: initialData.count.contacts_deleted })
                                : t('{{count}} contact of this mailing list has been deleted since its creation', { count: initialData.count.contacts_deleted })
                            }
                        </Alert>
                    )}
                    <ContactCreationForm 
                        mailingListId={id} 
                        onCreated={() => {
                            setTableKey(prev => prev + 1);
                            fetchData();
                        }} 
                    />
                </Box>
                : <FormSkeleton nb={4} />
            }

            {openedContact && (
                <Dialog open
                    onClose={handleClose}
                    aria-labelledby="parent-modal-title"
                >
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {isEditingContact && (
                        <DialogTitle>
                            {t('Edit contact')}
                        </DialogTitle>
                    )}
                    <DialogContent sx={{ width: '500px' }}>
                        {isEditingContact ? (
                            <ContactForm
                                ref={formRef}
                                contact={openedContact}
                                onSubmit={handleContactSubmit}
                            />
                        ) : (
                            <>
                                <ContactCard contact={openedContact} />
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => setIsEditingContact(true)}
                                    >
                                        {t('Edit')}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </Layout>
    );
}

export default MailingListEditPage;
